import { supabase } from '@/integrations/supabase/client';
import type { DashboardLeaveData, DashboardTimesheetData, LeaveRequest, TimeEntryWithProfile } from './types';
import { leavePolicyService } from '@/services/leave/leavePolicyService';

export const dashboardService = {
  getDashboardLeaveData: async (profileId: string): Promise<DashboardLeaveData> => {
    try {
      // Ensure user context is set before making queries that involve RLS policies
      await supabase.rpc('ensure_user_context');
      
      // Fetch real leave balances from the leave policy service
      const leaveBalances = await leavePolicyService.getLeaveBalances(profileId);
      
      // Fetch leave requests for the user
      const { data: leaveRequests, error: leaveError } = await supabase
        .from('leave_requests')
        .select('*')
        .eq('profile_id', profileId)
        .order('created_at', { ascending: false });

      if (leaveError) throw leaveError;

      // Get upcoming requests (approved or pending, future dates)
      const now = new Date().toISOString();
      const upcomingRequests = leaveRequests?.filter(request => 
        (request.status === 'approved' || request.status === 'pending') &&
        new Date(request.start_date) >= new Date(now)
      ).slice(0, 2) || [];

      // Get most recent request
      const recentRequest = leaveRequests?.[0] || null;

      return {
        leaveBalances,
        upcomingRequests: upcomingRequests as LeaveRequest[],
        recentRequest: recentRequest as LeaveRequest | null,
      };
    } catch (error) {
      console.error('Error fetching dashboard leave data:', error);
      throw error;
    }
  },

  getDashboardTimesheetData: async (profileId: string): Promise<DashboardTimesheetData> => {
    try {
      // Fetch the most recent time entry for the user
      const { data: timeEntries, error: timesheetError } = await supabase
        .from('time_entries')
        .select(`
          id,
          profile_id,
          clock_in,
          clock_out,
          status,
          created_at,
          profile:profiles!time_entries_profile_id_fkey (
            id,
            first_name,
            last_name,
            email,
            avatar_url
          )
        `)
        .eq('profile_id', profileId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (timesheetError) throw timesheetError;

      const recentEntry = timeEntries?.[0] as TimeEntryWithProfile | undefined;

      return {
        recentEntry: recentEntry || null,
      };
    } catch (error) {
      console.error('Error fetching dashboard timesheet data:', error);
      throw error;
    }
  },
};
